import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useAmplitudeExperiment } from 'contexts/AmplitudeExperimentContext';
import { FEATURE_FLAG_NAMES } from 'hooks/useFeatureFlag/constants';
import { useIsSyntheticsUser } from 'hooks/useIsSyntheticsUser';

import { STEPS } from './flow/constants';

const RegistrationContext = createContext(null);

function RegistrationProvider({ children }) {
    const { step } = useParams();
    const isValidStep = step != null && STEPS.includes(step);
    const is404 = step != null && !STEPS.includes(step);

    const { experimentClientIsReady, experimentClientIsTimedOut, getFeatureFlagVariant } = useAmplitudeExperiment();
    const isSyntheticsUser = useIsSyntheticsUser();

    const [showFlow, setShowFlow] = useState(isValidStep);
    const [currentStep, setCurrentStep] = useState(isValidStep ? step : null);

    const [showFlowLoadingOverlay, setShowFlowLoadingOverlay] = useState(false);

    const stepIndex = STEPS.indexOf(currentStep);
    const stepLength = STEPS.length;

    useEffect(() => {
        if (is404) {
            return;
        }

        if (isValidStep) {
            setCurrentStep(step);
            setShowFlow(true);
            return;
        }

        setShowFlow(false);
    }, [step, isValidStep, is404]);

    const featureFlagVariant =
        experimentClientIsReady && !isSyntheticsUser
            ? getFeatureFlagVariant(FEATURE_FLAG_NAMES.FUNNEL_REGISTRATION_TEST_V2, 'control')
            : undefined;

    const value = useMemo(
        () => ({
            currentStep,
            experimentClientIsReady,
            experimentClientIsTimedOut,
            is404,
            featureFlagVariant,
            setCurrentStep,
            setShowFlowLoadingOverlay,
            setShowFlow,
            showFlow,
            showFlowLoadingOverlay,
            stepIndex,
            stepLength,
        }),
        [
            currentStep,
            experimentClientIsReady,
            experimentClientIsTimedOut,
            is404,
            featureFlagVariant,
            showFlow,
            showFlowLoadingOverlay,
            stepIndex,
            stepLength,
        ]
    );

    return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>;
}

function useRegistrationContext() {
    const context = useContext(RegistrationContext);
    if (!context) {
        throw new Error('useRegistrationContext must be used within a RegistrationProvider');
    }
    return context;
}

export { RegistrationProvider, useRegistrationContext };
